import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

// Storage scheme: per-user keys so signed-in users don't share carts on a
// shared device. v2 supersedes the v1 single-key scheme; v1 keys are silently
// inherited once into the guest bucket on first load and then ignored.
const PREFIX = 'oh:cart:v2:';
const guestKey = () => PREFIX + 'guest';
const userKey  = (id) => PREFIX + 'u' + id;
const LEGACY_KEY = 'oh:cart:v1';

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) { return []; }
}
function write(key, items) {
  try { localStorage.setItem(key, JSON.stringify(items)); } catch (_) {}
}

// One-time migration: pull anything in the legacy global v1 key into v2 guest.
function migrateLegacy() {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const existing = read(guestKey());
    const merged = mergeItems(existing, JSON.parse(legacy) || []);
    write(guestKey(), merged);
    localStorage.removeItem(LEGACY_KEY);
  } catch (_) {}
}

// Merge two cart arrays: sum quantities for same id; preserve order.
function mergeItems(base, incoming) {
  const out = [...base];
  for (const inc of incoming || []) {
    if (!inc || inc.id == null) continue;
    const idx = out.findIndex((it) => String(it.id) === String(inc.id));
    if (idx >= 0) {
      out[idx] = { ...out[idx], quantity: (out[idx].quantity || 1) + (inc.quantity || 1) };
    } else {
      out.push(inc);
    }
  }
  return out;
}

export function CartProvider({ children }) {
  const { user } = useAuth() || {};
  const currentKey = useMemo(
    () => (user?.id ? userKey(user.id) : guestKey()),
    [user?.id]
  );
  const prevKeyRef = useRef(currentKey);

  const [items, setItems] = useState(() => {
    migrateLegacy();
    return read(currentKey);
  });

  // React to user changes: login merges guest cart into user cart; logout
  // (and user-switch) re-hydrates from the new key.
  useEffect(() => {
    const prev = prevKeyRef.current;
    if (prev === currentKey) return;

    const prevIsGuest = prev === guestKey();
    const nextIsUser  = currentKey !== guestKey();

    if (prevIsGuest && nextIsUser) {
      const merged = mergeItems(read(currentKey), read(guestKey()));
      write(currentKey, merged);
      write(guestKey(), []);
      setItems(merged);
    } else {
      setItems(read(currentKey));
    }
    prevKeyRef.current = currentKey;
  }, [currentKey]);

  // Persist on every items change to the current key.
  useEffect(() => {
    write(currentKey, items);
  }, [items, currentKey]);

  // Cross-tab sync: only react to the key we currently own.
  useEffect(() => {
    function onStorage(e) {
      if (e.key === currentKey) setItems(read(currentKey));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [currentKey]);

  const addItem = (product, quantity = 1) => {
    if (!product) return;
    const variationId = product.variationId || null;
    setItems((cur) => {
      // Match by BOTH product id AND variationId — different colors / sizes
      // of the same parent product are different lines in the cart.
      const idx = cur.findIndex((it) =>
        String(it.id) === String(product.id) &&
        String(it.variationId || '') === String(variationId || '')
      );
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + quantity };
        return next;
      }
      return [...cur, {
        id: product.id,
        // Variable products: variationId identifies the specific SKU + attribute
        // combo. selectedAttributes is the human-readable label set
        // ({color: 'White', size: 'M'}). Both flow through to /checkout so the
        // WC order's line item ends up on the exact variation.
        variationId,
        selectedAttributes: product.selectedAttributes || null,
        name: product.name,
        brand: product.brand || '',
        price: typeof product.price === 'number' ? product.price : 0,
        originalPrice: product.originalPrice ?? null,
        image: product.images?.[0]?.url || product.image || product.image_url || '',
        imageAlt: product.imageAlt || product.images?.[0]?.alt || product.name || '',
        quantity,
        inStock: product.inStock !== false,
        stockLevel: product.stockCount ?? product.stockLevel ?? null,
      }];
    });
  };

  const updateQuantity = (id, quantity) => {
    setItems((cur) => cur.map((it) =>
      String(it.id) === String(id) ? { ...it, quantity: Math.max(1, quantity) } : it
    ));
  };
  const removeItem = (id) => setItems((cur) => cur.filter((it) => String(it.id) !== String(id)));
  const clearCart  = () => setItems([]);

  const totalCount = items.reduce((sum, it) => sum + (it.quantity || 1), 0);
  const totalCents = Math.round(items.reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 1) * 100, 0));

  return (
    <CartContext.Provider value={{ items, addItem, updateQuantity, removeItem, clearCart, totalCount, totalCents }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
